<template>
  <div>
    <el-card
      shadow="hover"
      class="card mb-3 permissionse-card"
      style="width: 100%"
      v-loading="loading"
      element-loading-text="Loading..."
      element-loading-spinner="el-icon-loading"
      element-loading-background="rgba(0, 0, 0, 0.8)"
    >
      <div class="card-header bg-light">
        <h3 class="title fw-normal mb-0 p-2 !text-black-950">Global Settings</h3>
      </div>

      <div class="p-3">
        <p class="toggle-label">MFA (Multi-Factor Authentication)</p>
        <el-switch
          v-model="mfaEnabled"
          active-text="Enabled"
          inactive-text="Disabled"
          @change="handleToggleChange"
        />
      </div>

      <div v-if="mfaEnabled" class="p-3">
        <el-row :gutter="20" align="middle">
          <el-col :sm="12" :xs="24">
            <p class="mb-2">
              Select User Roles
              <span style="color: red">*</span>
            </p>
            <el-select
              v-model="selectedRoles"
              multiple
              placeholder="Select User Roles"
              style="width: 100%"
            >
              <el-option
                v-for="item in userTypes"
                :key="item._id"
                :label="getLabel(item)"
                :value="item._id"
              />
            </el-select>
          </el-col>

          <el-col :sm="12" :xs="24">
            <p class="mb-2">
              Select Login Type
              <span style="color: red">*</span>
            </p>
            <el-radio-group v-model="loginType">
              <!-- <el-radio label="PASSWORD">Password</el-radio>
              <el-radio label="OTP">OTP</el-radio>
              <el-radio label="PASSWORD_OTP">Password + OTP (MFA)</el-radio> -->

              <el-radio label="PASSWORD" :disabled="true">Password</el-radio>
              <el-radio label="PASSWORD_OTP" :disabled="true">OTP</el-radio>
              <el-radio label="OTP">Password + OTP (MFA)</el-radio>
            </el-radio-group>
          </el-col>
        </el-row>

        <div class="save-btn-container">
          <el-button
            type="primary"
            :disabled="!selectedRoles.length || !loginType"
            @click="saveMFASettings"
          >
            Save
          </el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import Swal from 'sweetalert2';
  import { postAPICall } from '@/helpers/httpHelper';

  export default {
    name: 'MFASettings',
    data() {
      return {
        loading: false,
        mfaEnabled: false,
        userTypes: [],
        selectedRoles: [],
        loginType: '',
      };
    },
    computed: {
      ...mapGetters('auth', ['getUserTypeList', 'getActiveWorkspace']),
      getLabel() {
        return (userType) => {
          if (userType && userType.role) {
            return `${userType.role.title} - ${userType.name}`;
          } else {
            return userType.name;
          }
        };
      },
    },
    mounted() {
      this.fetchCompanyMFASettings();
      this.getCompanyUserTypeList();
    },
    methods: {
      async fetchCompanyMFASettings() {
        try {
          this.loading = true;
          const id =
            this.getActiveWorkspace?.company_id?._id || this.getActiveWorkspace?.company_id;
          const res = await postAPICall('GET', `/company/${id}`);
          if (res?.data?.mfa_settings) {
            this.mfaEnabled = res.data.mfa_settings.enabled;
            this.selectedRoles = res.data.mfa_settings.role_ids || [];
            this.loginType = res.data.mfa_settings.login_type || '';
          }
        } catch (err) {
          console.error('Error fetching MFA settings', err);
        } finally {
          this.loading = false;
        }
      },

      async getCompanyUserTypeList() {
        this.loading = true;
        try {
          const companyId =
            this.getActiveWorkspace?.company_id ||
            this.getAuthenticatedUser?.current_workspace?.company_id;

          const res = await postAPICall('GET', '/userTypeListMfa', {
            company_id: companyId,
          });

          const list = Array.isArray(res?.data?.data)
            ? res.data.data
            : Array.isArray(res?.data)
              ? res.data
              : [];

          this.userTypes = list;
        } catch (err) {
          this.$notify.error({
            title: 'Error',
            message: err?.response?.data?.message || err?.message || 'Failed to fetch user types',
          });
        } finally {
          this.loading = false;
        }
      },

      handleToggleChange(val) {
        if (!val) {
          this.selectedRoles = [];
          this.loginType = '';
          this.saveMFASettings(true);
        }
      },
      async saveMFASettings() {
        try {
          this.loading = true;

          const companyId =
            this.getActiveWorkspace?.company_id?._id || this.getActiveWorkspace?.company_id;

          // Validate if MFA is enabled
          if (this.mfaEnabled) {
            if (!this.selectedRoles.length) {
              Swal.fire({
                icon: 'warning',
                title: 'Please select at least one role.',
                timer: 2000,
              });
              this.loading = false;
              return;
            }
            if (!this.loginType) {
              Swal.fire({
                icon: 'warning',
                title: 'Please select a login type.',
                timer: 2000,
              });
              this.loading = false;
              return;
            }
          }

          const updatedCompany = {
            ...this.companyDetails,
            _id: companyId,
            mfa_settings: {
              enabled: this.mfaEnabled,
              role_ids: this.mfaEnabled ? [...this.selectedRoles] : [],
              login_type: this.mfaEnabled ? this.loginType : '',
            },
          };

          await postAPICall('PATCH', '/company', updatedCompany);

          Swal.fire({
            icon: 'success',
            title: 'MFA settings updated successfully',
            timer: 2000,
          });

          await this.fetchCompanyMFASettings();
        } catch (err) {
          Swal.fire({
            icon: 'error',
            title: err?.response?.data?.message || 'Error saving MFA settings',
          });
        } finally {
          this.loading = false;
        }
      },
    },
  };
</script>

<style scoped>
  .p-3 {
    padding: 1rem;
  }
  .mb-2 {
    margin-bottom: 0.5rem;
  }
  .toggle-label {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 0.5rem;
  }
  .save-btn-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
  }
</style>
